<template>
  <v-card dark class="d-flex flex-column" style="background-color: #121212; min-height: 100vh; border-radius: 0">
    <v-toolbar dark color="#1a1a1a" flat fixed style="border-bottom: 1px solid #2a2a2a; z-index: 10">
      <v-btn icon @click="checkChanges(0)"><v-icon>mdi-close</v-icon></v-btn>
      <v-toolbar-title class="text-h6 font-weight-bold white--text ml-2 tracking-wide">
        {{ form._id ? "Gerät bearbeiten" : "Neues Gerät erstellen" }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        color="#00E676"
        class="black--text font-weight-bold px-6 tracking-wide"
        depressed
        :loading="isSaving"
        :disabled="isSaving"
        @click="validateForm">
        SPEICHERN
      </v-btn>
    </v-toolbar>

    <div style="overflow-y: auto; flex-grow: 1; padding-bottom: 100px">
      <div style="background-color: #121212; padding: 24px 0">
        <v-card dark color="transparent" class="elevation-0 mx-auto" max-width="900">
          <div class="slim-wrapper">
            <slim-cropper v-if="loaded" style="height: 350px; width: 100%; border-radius: 8px; overflow: hidden" :options="slimOptions">
              <img v-if="form.picture" :src="'/img/uebungen/' + form.picture" alt="" />
              <input type="file" accept="image/*" />
            </slim-cropper>
          </div>
          <div
            v-if="!form.picture && loaded"
            class="text-center pa-8 grey--text"
            style="
              border: 1px dashed #333;
              border-radius: 8px;
              margin-top: -350px;
              height: 350px;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              pointer-events: none;
            ">
            <v-icon large color="grey darken-2" class="mb-2">mdi-camera-plus</v-icon>
            <span>Tippe hier, um ein Bild hochzuladen</span>
          </div>
        </v-card>
      </div>

      <v-form ref="form" lazy-validation class="pa-4 mx-auto" style="max-width: 900px">
        <v-row class="mb-2">
          <v-col cols="12" md="8">
            <v-text-field
              label="Geräte Name"
              v-model="form.title"
              :rules="rules.title"
              required
              outlined
              dark
              dense
              background-color="#1a1a1a"
              class="modern-input font-weight-medium"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Kurzform"
              v-model="form.shortForm"
              :rules="rules.shortForm"
              required
              counter="8"
              outlined
              dark
              dense
              background-color="#1a1a1a"
              class="modern-input font-weight-medium"></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mb-2">
          <v-col cols="12">
            <v-combobox
              v-model="form.category"
              :items="categories"
              label="Kategorie"
              outlined
              dark
              dense
              background-color="#1a1a1a"
              class="modern-input"></v-combobox>
          </v-col>
        </v-row>

        <v-row class="mb-8">
          <v-col cols="12">
            <v-select
              v-model="form.studios"
              :items="studios"
              item-text="name"
              item-value="_id"
              label="In welchen Studios anzeigen?"
              multiple
              outlined
              dark
              dense
              background-color="#1a1a1a"
              class="modern-input">
              <template v-slot:selection="{ item }">
                <v-chip dark small color="#00E676" class="black--text font-weight-bold mt-1 mr-1">{{ item.name }}</v-chip>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-divider dark style="border-color: #2a2a2a" class="my-6"></v-divider>

        <div class="d-flex justify-space-between align-center mb-6">
          <h2 class="white--text font-weight-bold text-h5">Übungen am Gerät</h2>
          <v-btn color="#00E676" class="black--text px-4 font-weight-bold tracking-wide" depressed small @click="addExercise">
            <v-icon left small>mdi-plus</v-icon> NEUE ÜBUNG
          </v-btn>
        </div>

        <v-card
          v-for="(exercise, index) in form.exercises"
          :key="index"
          v-if="exercise"
          dark
          color="#1a1a1a"
          class="mb-3 d-flex flex-row align-center elevation-0"
          style="border: 1px solid #2a2a2a; border-radius: 6px">
          <div
            style="width: 80px; height: 80px; background-color: #121212; border-radius: 6px 0 0 6px; overflow: hidden"
            class="d-flex justify-center align-center">
            <v-img v-if="exercise.picture" :src="'/img/uebungen/' + exercise.picture" cover height="100%" width="100%"></v-img>
            <v-icon v-else color="grey darken-2">mdi-dumbbell</v-icon>
          </div>

          <div class="pa-4 flex-grow-1">
            <div class="text-subtitle-1 font-weight-bold white--text">{{ exercise.title }}</div>
            <div class="text-caption grey--text mt-1">
              {{ exercise.general_exercise_settings ? exercise.general_exercise_settings.length : 0 }} Gerät. |
              {{ exercise.set_exercise_settings ? exercise.set_exercise_settings.length : 0 }} Satz.
            </div>
          </div>

          <div class="pa-4 d-flex">
            <v-btn
              v-if="userService.hasRight('ex_edit')"
              color="blue"
              outlined
              small
              class="mr-2"
              style="min-width: 36px; padding: 0"
              @click="editExercise(exercise, index)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              v-if="userService.hasRight('ex_transfer')"
              color="orange"
              outlined
              small
              class="mr-2"
              style="min-width: 36px; padding: 0"
              @click="reorderExercise(exercise)">
              <v-icon small>mdi-swap-horizontal</v-icon>
            </v-btn>
            <v-btn
              v-if="userService.hasRight('ex_delete')"
              color="red"
              outlined
              small
              @click="deleteExercise(exercise, 0)"
              style="min-width: 36px; padding: 0">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-card>

        <div
          v-if="!form.exercises || form.exercises.length === 0"
          class="text-center pa-6 grey--text darken-1 text-subtitle-1"
          style="border: 1px dashed #333; border-radius: 6px">
          Noch keine Übungen für dieses Gerät angelegt.
        </div>
      </v-form>
    </div>

    <v-dialog v-model="exerciseDialog" persistent fullscreen hide-overlay transition="dialog-bottom-transition" dark>
      <DeviceDashboardExerciseForm
        v-if="exerciseDialog"
        @close="
          exerciseDialog = false
          updateExercise()
        " />
    </v-dialog>

    <v-dialog v-model="saveDialog" max-width="400" dark>
      <v-card color="#1a1a1a">
        <v-card-title class="text-h6 font-weight-bold white--text pt-5">Ungespeicherte Änderungen!</v-card-title>
        <v-card-text class="grey--text mt-2">Möchtest Du ohne zu speichern schliessen?</v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="grey" text @click="$emit('close')">Verwerfen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#00E676" class="black--text font-weight-bold px-4" depressed :loading="isSaving" :disabled="isSaving" @click="validateForm"
            >Speichern</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showReorderDialog" persistent max-width="400" dark>
      <v-card color="#1a1a1a" style="border-radius: 8px">
        <v-card-title class="text-h6 font-weight-bold white--text pt-5">Übung verschieben</v-card-title>
        <v-card-text class="pt-4">
          Wähle ein Gerät aus, in das die Übung verschoben werden soll.
          <v-select
            v-model="reorderDeviceId"
            :items="categorizedDevices"
            item-text="title"
            item-value="_id"
            label="Ziel-Gerät auswählen"
            outlined
            dense
            dark
            background-color="#121212"
            class="modern-input mt-4"></v-select>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="grey" text small @click="showReorderDialog = false">Abbrechen</v-btn>
          <v-btn color="orange" class="black--text font-weight-bold px-4" depressed small @click="saveReorderExercise">Verschieben</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService" // WICHTIG: userService wieder importiert
import { mapGetters, mapActions, mapState } from "vuex"
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import DeviceDashboardExerciseForm from "./devicesDashboardExerciseForm.vue"

export default {
  name: "deviceForm",
  components: {
    "slim-cropper": Slim,
    DeviceDashboardExerciseForm,
  },
  props: ["deviceId"],
  data() {
    return {
      isSaving: false, // <--- NEU
      userService, // Für die hasRight abfragen
      categories: [],
      studios: [],
      categorizedDevices: [], // Für das Dropdown beim Verschieben
      loaded: false,
      oldForm: {},
      saveDialog: false,
      exerciseDialog: false,

      showReorderDialog: false,
      reorderedExercise: null,
      reorderDeviceId: null,

      slimOptions: {
        ratio: userService.hasRight("g_showHiddenStudio") ? "free" : "4:3",
        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        label: "",
        dataJpegCompression: 30,
        service: this.uploadPicture,
        instantEdit: true,
        serviceFormat: "file",
        push: true,
      },
      form: {
        title: "",
        shortForm: "",
        exercises: [],
        studios: [],
        category: "",
        picture: null,
      },
      rules: {
        title: [(v) => !!v || "Titel wird benötigt"],
        shortForm: [(v) => !!v || "Kurzform wird benötigt", (v) => v.length <= 8 || "Maximal 8 Zeichen"],
      },
    }
  },
  async created() {
    this.studios = await api.fetchStudios()
    this.categories = await api.fetchDeviceCategories()
    this.resetEditExercise()

    if (this.deviceId) {
      this.form = await api.fetchDevice(this.deviceId)
      this.oldForm = JSON.stringify(this.form)
      this.setDevice(this.form)
    }
    this.loaded = true
  },
  methods: {
    ...mapActions("deviceStore", ["setDevice", "setEditExercise", "resetEditExercise", "removeExercise"]),

    checkChanges(status = 0) {
      if (this.oldForm !== JSON.stringify(this.form)) {
        this.saveDialog = true
      } else {
        this.$emit("close")
      }
    },

    async validateForm() {
      if (this.isSaving) return
      if (!this.$refs.form.validate()) {
        this.saveDialog = false
        return
      }
      this.isSaving = true
      try {
        if (this.form._id) {
          await api.updateDevice(this.form)
        } else {
          await api.createDevice(this.form)
        }
        this.$emit("saved")
        this.$emit("close")
      } catch (error) {
        this.saveDialog = false
        const status = error.response && error.response.status
        if (status === 401 || status === 403) {
          this.$toast.error("Speichern nicht erlaubt. Bitte Anmeldung und Berechtigungen prüfen.")
        } else if (!error.response) {
          this.$toast.error("Keine Speicherbestätigung erhalten. Bitte zuerst in der Geräteliste prüfen, ob die Änderung bereits gespeichert ist.")
        } else {
          this.$toast.error("Das Gerät konnte nicht gespeichert werden. Deine Eingaben bleiben erhalten.")
        }
      } finally {
        this.isSaving = false
      }
    },

    addExercise() {
      this.resetEditExercise()
      this.exerciseDialog = true
    },

    editExercise(exercise, index) {
      exercise.index = index
      this.setEditExercise(exercise)
      this.exerciseDialog = true
    },

    deleteExercise(exercise, status) {
      if (confirm("Übung wirklich löschen?")) {
        this.removeExercise(exercise)
        this.form.exercises = this.exercises
      }
    },

    // --- ÜBUNG VERSCHIEBEN ---
    async reorderExercise(exercise) {
      // Lade alle Geräte für das Dropdown, falls noch nicht passiert
      if (!this.categorizedDevices.length > 0) this.categorizedDevices = await api.fetchDevices()
      this.showReorderDialog = true
      this.reorderedExercise = exercise
      this.reorderDeviceId = null
    },

    async saveReorderExercise() {
      if (!this.reorderDeviceId) return alert("Bitte Ziel-Gerät auswählen")
      this.showReorderDialog = false

      // Lokales Entfernen der Übung aus dem aktuellen Formular
      this.removeExercise(this.reorderedExercise)
      this.form.exercises = this.exercises

      // Bereinigen der ID für den Server, damit es kein Konflikt gibt
      delete this.reorderedExercise._id
      await api.reorderExercise(this.reorderDeviceId, this.reorderedExercise)

      this.reorderDeviceId = null
      this.reorderedExercise = null
      alert("Übung erfolgreich verschoben!")
    },

    uploadPicture(formdata, progress, success, failure) {
      const formData = new FormData()
      formData.append("file", formdata[0])
      let uploadimg = { art: "device", formData: formData }
      api
        .uploadImage(uploadimg)
        .then((data) => {
          if (data.status) {
            this.form.picture = data.name
            success(data)
          }
        })
        .catch(failure)
    },

    updateExercise() {
      this.form.exercises = this.exercises
      this.resetEditExercise()
    },
  },
  computed: {
    ...mapGetters("deviceStore", ["exercises"]),
    ...mapState("deviceStore", ["device", "exerciseEdit"]),
  },
}
</script>

<style scoped>
.tracking-wide {
  letter-spacing: 1px;
}
.modern-input ::v-deep .v-input__slot {
  border-color: #333 !important;
}
.modern-input ::v-deep .v-input--is-focused .v-input__slot {
  border-color: #00e676 !important;
}

/* FIX FÜR DEN SLIM CROPPER (Nur Icons, kein Text) */
.slim-wrapper ::v-deep .slim-btn-group {
  display: flex !important;
  flex-direction: row !important;
  gap: 10px;
  justify-content: center;
}

.slim-wrapper ::v-deep .slim-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: rgba(30, 30, 30, 0.8) !important;
  border-radius: 6px !important;
  width: 40px !important; /* Feste Breite für quadratische Buttons */
  height: 40px !important; /* Feste Höhe */
  padding: 0 !important; /* Padding weg, damit es zentriert ist */
  color: white !important;
}

/* Den Text (span) komplett ausblenden */
.slim-wrapper ::v-deep .slim-btn span {
  display: none !important;
}

/* Margin beim Icon entfernen, da kein Text mehr daneben steht */
.slim-wrapper ::v-deep .slim-btn svg {
  margin: 0 !important;
}
/* Verhindert, dass man beim Scroll-Bounce (Herumschieben) den Hintergrund sieht */
::v-deep .v-dialog--fullscreen {
  background-color: #121212 !important;
}

/* Entfernt jegliche Transparenz aus den Formularen */
.v-dialog .v-card {
  opacity: 1 !important;
}
</style>
